import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BatteryLow, CircleAlert, WifiOff } from "lucide-react";
import { HoverPopover } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  getReaderLiveSnapshot,
  readerLiveStateLabelKey,
  readerLiveStatusClassification,
} from "@/lib/readerDeviceStatus";
import type { DeviceReaderSummary } from "@/types";

/** Battery percent at or below this shows a low-battery alert. */
const LOW_BATTERY_LEVEL = 25;

type AlertIconProps = {
  label: string;
  className: string;
  children: ReactNode;
};

function AlertIcon({ label, className, children }: AlertIconProps) {
  return (
    <HoverPopover placement="above" content={label}>
      <span
        className={cn(
          "-m-0.5 inline-flex shrink-0 cursor-default items-center justify-center rounded-md p-0.5",
          className,
        )}
        aria-label={label}
      >
        {children}
      </span>
    </HoverPopover>
  );
}

/**
 * Reader problem icons only (no always-on status badge).
 * Offline → Wi-Fi off; unavailable → error; low battery → battery.
 */
export function ReaderLiveStatusDot({
  reader,
}: {
  reader: DeviceReaderSummary;
}) {
  const { t } = useTranslation("readers");
  const liveBucket = readerLiveStatusClassification(reader);
  const snap = getReaderLiveSnapshot(reader);

  const batteryRounded =
    snap?.battery_level != null ? Math.round(snap.battery_level) : null;
  const showLowBattery =
    batteryRounded != null && batteryRounded <= LOW_BATTERY_LEVEL;
  const showOffline = liveBucket === "offline";
  const showUnavailable = liveBucket === "unavailable";

  if (!showOffline && !showUnavailable && !showLowBattery) {
    return null;
  }

  const stateLabel =
    snap && liveBucket && liveBucket !== "unknown"
      ? t(readerLiveStateLabelKey(snap, liveBucket))
      : null;

  return (
    <span className="inline-flex shrink-0 items-center gap-0.5">
      {showOffline ? (
        <AlertIcon
          label={stateLabel ?? t("page.deviceOffline")}
          className="text-error-600"
        >
          <WifiOff className="h-3.5 w-3.5" aria-hidden />
        </AlertIcon>
      ) : null}
      {showUnavailable ? (
        <AlertIcon
          label={stateLabel ?? t("page.deviceState.UNKNOWN")}
          className="text-warning-600"
        >
          <CircleAlert className="h-3.5 w-3.5" aria-hidden />
        </AlertIcon>
      ) : null}
      {showLowBattery ? (
        <AlertIcon
          label={t("page.deviceBattery", { level: batteryRounded })}
          className="text-error-600"
        >
          <BatteryLow className="h-3.5 w-3.5" aria-hidden />
        </AlertIcon>
      ) : null}
    </span>
  );
}
